import { RawChartSchema, SortOption, SortType } from 'common/types/chart';
import { Card } from 'common/types/dashboard';
import { ICONS } from 'icons/iconsMap';
import { DataTestId } from 'settings/constants';
import { Folder } from './folders';
import { SelectPopupItemProps } from './selectMenu';

export enum ITEM_TYPES {
  FOLDER = 'folder',
  ITEM = 'item',
}

export interface Wave {
  id: number;
  name: string;
  date: Date;
  color?: string;
}

/**
 * Resolves a wave id to its wave. Keyed by both raw wave ids and cadence entry ids, because
 * `normalizeRowsWave` rewrites `RawChartData.wave` to a cadence entry id on a cadence chart and
 * `useGetWavesAndCadencesMap` puts both kinds of key into the one map.
 */
export type WaveMap = Record<number | string, Wave>;

interface WaveAvailability {
  available_for_all_waves: boolean;
  wave_ids: number[];
}

export interface Brand {
  id: number;
  name: string;
  logo: string;
  global?: boolean;
  wave_availability: WaveAvailability;
}

export interface BrandPayload {
  id: number;
  name: string;
  global: boolean;
  primary: boolean;
}

export interface Country {
  id: number;
  code: string;
  name: string;
  color?: string;
  population?: number;
}

export interface WaveReport {
  id: number;
  title: string;
  link_to_pdf: string;
  created_at: string;
  is_new: boolean;
  period: Date | string;
}

export interface WaveSummary {
  study_uuid: string;
  wave_summaries: WaveReport[];
}

export interface PopoverItemProps {
  name: string;
  action?: () => void;
  showToggle?: boolean;
  toggleEnabled?: boolean;
  toggleAction?: (enabled: boolean) => void;
  tooltip?: React.ReactNode;
}

export interface FetchOptions {
  method?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: Record<string, any> | RawChartSchema;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query?: Record<string, any>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ChartFields {
  country: (string | number)[];
  wave: (string | number)[];
  kpi: (string | number)[];
  brand: (string | number)[];
  segment: (string | number)[];
}

export type IFetchMethod = <T>(url: string, query?: FetchOptions['query']) => Promise<T>;

export interface IFetchMethods {
  fetchGet: <T>(url: string, query?: FetchOptions['query'], signal?: AbortSignal) => Promise<T>;
  fetchPost: <T>(url: string, body?: FetchOptions['body'], options?: { query: FetchOptions['query'] }) => Promise<T>;
  fetchPatch: <T>(url: string, body: FetchOptions['body'], options?: { query: FetchOptions['query'] }) => Promise<T>;
  fetchPut: <T>(url: string, body?: FetchOptions['body'], options?: { query: FetchOptions['query'] }) => Promise<T>;
  fetchDelete: <T>(url: string, query?: FetchOptions['query']) => Promise<T>;
}

export enum CSVExportStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  PENDING = 'pending',
}

export type MoeModalDataProps = {
  firstDimensionId: number;
  secondDimensionId: string;
  populationValue: number;
  /** Absolute margin of error: margin of error x segment population (not x estimated population). */
  moePopulationValue: number;
};

export type BrandColorMapType = Record<string, string>;
export type SegmentColorMapType = Record<string, string>;

type BrandByCountries = Record<string, Array<number>>;
export interface BrandGroups {
  id: number | string;
  name: string;
  group_type: BrandGroupType;
  brand_ids_by_country: BrandByCountries;
}

export enum BrandGroupType {
  PRIMARY = 'primary',
  COMPETITORS = 'competitors',
}

export type CountryPayload = {
  study_uuid: string;
  countries: Array<Country>;
};

export type Owner = {
  name: string;
  type: string;
};

export interface TableListItemProps {
  type?: ITEM_TYPES;
  name: string;
  uuid: string;
  updated_at: string;
  owner?: Owner;
  current_view?: string;
  chart_type?: string;
  // Only if items are chart list items
  study_uuid?: string;
  cards?: Card[];
  tags?: string[];
  folders: Folder[];
  currentFolderId?: string;
}

export enum SortingKey {
  NAME = 'name',
  OWNER = 'owner',
  UPDATED_AT = 'updated_at',
}

export type SortingCriteria = {
  key: SortingKey;
  type: SortType;
};

export interface TableRowItemProps {
  listItemData: TableListItemProps;
  style: React.CSSProperties;
  onClick: (chartId: string, studyId: string) => void;
  onSelect: (actions: string[], item: TableListItemProps) => void;
  customActions?: SelectPopupItemProps[];
}

export type HTMLTag = 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'dt' | 'dd'; // Add more tags if needed

export interface NavButtonProps {
  icon: keyof typeof ICONS;
  label: string;
  disabled?: boolean;
  action?: () => void;
  url?: string;
  divider?: boolean;
  testId?: DataTestId;
}

export enum LegendItemStatus {
  DEFAULT = 'DEFAULT',
  CHECKED = 'CHECKED',
  UNCHECKED = 'UNCHECKED',
}

export interface FolderItemProps {
  id: string;
  name: string;
  chartIds: string[];
  isOpen?: boolean;
}

export interface IconItemProps {
  icon: string;
  active?: boolean;
  withLabel?: boolean;
  centeredIconTrigger?: boolean;
}

export interface DropDownItemProps<T> {
  value: T;
  label: string;
  icon?: string;
  disabled?: boolean;
  description?: string;
}

export enum DropdownAlign {
  LEFT = 'left',
  RIGHT = 'right',
}

export interface DropdownWithIconProps<T> {
  label: string;
  testId?: string;
  topLabel?: string;
  noHover?: boolean;
  withArrow?: boolean;
  toolbarIcon: string;
  align: DropdownAlign;
  defaultText?: string;
  withBorder?: boolean;
  inDashboard?: boolean;
  withTopLabel?: boolean;
  renderInBody?: boolean;
  extraPadding?: boolean;
  showDefaultText?: boolean;
  showIconAndLabel?: boolean;
  noActiveDefaultText?: boolean;
  options: DropDownItemProps<T>[];
  onSelect: <T>(value: T) => void;
  selectedValue: string | SortOption | null;
  noLabel?: boolean;
  borderRadius?: string;
  height?: string;
  borderColor?: string;
  colour?: string;
  fontWeight?: string;
  padding?: string;
  centeredIconTrigger?: boolean;
}
